import { useMemo, useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Equal endpoints
 * collapse to a single value (e.g. "2 - 2" → "2 Bedroom",
 * "Studio - Studio" → "Studio").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse equal endpoints: "2 - 2" → "2", "Studio - Studio" → "Studio".
  // Compare case-insensitively so "studio - Studio" still collapses cleanly.
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    <div className="relative w-full z-10 bg-[var(--vibe-color-background)]">
    <section
      ref={sectionRef}
      className="relative w-full h-[100vh] overflow-hidden"
      style={{
        // Downward-pointing chevron: the bottom edge slopes from each side
        // down to a single point at the horizontal centre, revealing the
        // white page background as two triangular wedges.
        //
        // The section height was trimmed (was 120vh, briefly 90vh) so the
        // section sits at about one viewport, letting the following section
        // peek above the fold without feeling cramped. The chevron dip was deepened (sides 76%, tip 90% → a 14%
        // drop, was 10%) so the wedge angle stays crisp — and slightly
        // steeper — at the shorter height instead of flattening out.
        //
        // overflow-hidden (not clip-path) is what contains the oversized
        // parallax wrapper: it bleeds 1% past each edge (see below), and
        // clip-path only clips *painting*, not *scrollable overflow* — so
        // without overflow-hidden that right-side bleed widens the page and
        // causes a horizontal scroll. clip-path still shapes the chevron.
        clipPath: 'polygon(0 0, 100% 0, 100% 76%, 50% 90%, 0 76%)',
      }}
    >
      {/* Parallax + Ken Burns animated background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
            and ~2 % wider than the section (1 % bleed on each side). The
            extra height gives the parallax translate room to move, and the
            extra width absorbs the Ken Burns horizontal translate (-0.8 %)
            so no gap is exposed on the right edge. */}
        <div ref={imgWrapRef} className="absolute h-[140%]" style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}>
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Primary-color overlay at 77% opacity, layered above the parallax
          image (which sits in an un-z-indexed wrapper) but below the content
          (z-10) so text and CTA stay fully legible. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 z-0 pointer-events-none"
        style={{ background: 'var(--vibe-color-primary)', opacity: 0.77 }}
      />

      {/* Content. The flex container fills the section (h-full) so children
          can be vertically centered, but we disable pointer events on the
          wrapper itself and re-enable them only on the actual interactive
          children. That way empty space in the hero passes clicks through
          to anything floating on top (e.g. the RentCafe CRM IQ chatbot). */}
      <div className="relative z-10 flex flex-col items-center justify-center translate-y-20 h-full text-center text-white px-6 sm:px-8 pointer-events-none">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-glow hero-title-header-font text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg uppercase pointer-events-auto"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-3 font-body text-lg sm:text-xl md:text-2xl max-w-5xl mb-10 leading-relaxed opacity-90 drop-shadow-md uppercase pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            // mb-28 opens space beneath the button so the bottom-anchored
            // vertical accent line (bottom 2%, h-16%) no longer runs up into
            // the CTA. The content column is justify-center, so this trailing
            // margin lifts the button clear of the line at the chevron tip.
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 mb-28 text-sm font-normal tracking-[0.04em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        .hero-cta {
          /* Match the demo site's hero button typeface. The reference uses
             "Van Condensed Pro" (a proprietary, non-loadable Duda font), so
             we approximate it with Oswald — the closest free condensed sans,
             loaded by the Header via the css2 link. */
          font-family: 'Oswald', 'Arial Narrow', system-ui, sans-serif;
          background: var(--vibe-color-secondary);
          border: 1.5px solid var(--vibe-color-secondary);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(255, 255, 255, 0);
          transition: background 0.3s ease;
        }
        .hero-cta:hover {
          background: #fff;
          border-color: #fff;
          color: var(--vibe-color-secondary);
          box-shadow: none;
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          background: rgba(255, 255, 255, 0);
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        /* Match the header's navigation typeface (Space Mono, loaded by the
           Header via the css2 link). The header nav, phone, and utility links
           all render in Space Mono, so the hero title uses the same family to
           read as one consistent voice. Space Mono ships only 400 + 700; the
           title carries font-normal (400), a real cut, so no weight rounding. */
        .hero-title-header-font {
          font-family: 'Space Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
        }
        .hero-title-glow {
          text-shadow:
            0 0 12px rgba(255, 255, 255, 0.35),
            0 0 30px rgba(255, 255, 255, 0.18);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>

    {/* Vertical accent line centred on the chevron tip — the clip-path dips
        to its lowest point (90% of the hero height) at the horizontal centre,
        which is exactly where the image meets the white space along this
        column. With h-[16%] centred there, the top half (82%–90%) overlaps
        the image and the bottom half (90%–98%) drops onto the white below.
        Lives in the wrapper (not the clipped section) so the chevron clip-path
        doesn't cut it off; the wrapper's z-10 lifts it above the following
        section's background. */}
    <div
      aria-hidden="true"
      className="absolute left-1/2 -translate-x-1/2 w-[3px] h-[16%] z-20"
      style={{ background: 'var(--vibe-color-secondary)', bottom: '2%' }}
    />
    </div>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
